import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, CheckCircle, AlertTriangle, Scale, Clock, Ban, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Helmet } from 'react-helmet-async';

const TermsOfService = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const sections = [
    {
      icon: CheckCircle,
      title: isRTL ? 'قبول الشروط' : 'Acceptance of Terms',
      content: isRTL 
        ? 'باستخدام خدمات فلوفيد ERP، فإنك توافق على الالتزام بهذه الشروط والأحكام. إذا كنت لا توافق على أي جزء من هذه الشروط، فلا يجوز لك استخدام خدماتنا.'
        : 'By using FlowFeed ERP services, you agree to be bound by these terms and conditions. If you disagree with any part of these terms, you may not use our services.'
    },
    {
      icon: FileText,
      title: isRTL ? 'وصف الخدمة' : 'Service Description',
      content: isRTL
        ? 'يوفر فلوفيد ERP منصة سحابية لإدارة موارد المؤسسات تشمل إدارة علاقات العملاء، المشاريع، المالية، الموارد البشرية، والمخزون. نحتفظ بالحق في تعديل أو إيقاف الخدمات في أي وقت.'
        : 'FlowFeed ERP provides a cloud-based enterprise resource planning platform including CRM, project management, finance, HR, and inventory. We reserve the right to modify or discontinue services at any time.'
    },
    {
      icon: Scale,
      title: isRTL ? 'مسؤوليات المستخدم' : 'User Responsibilities',
      content: isRTL
        ? 'أنت مسؤول عن الحفاظ على سرية حسابك، وجميع الأنشطة التي تتم تحت حسابك، وضمان دقة المعلومات المقدمة، والامتثال للقوانين المعمول بها.'
        : 'You are responsible for maintaining account confidentiality, all activities under your account, ensuring accuracy of provided information, and compliance with applicable laws.'
    },
    {
      icon: Ban,
      title: isRTL ? 'الاستخدام المحظور' : 'Prohibited Use',
      content: isRTL
        ? 'يُحظر استخدام الخدمة لأي غرض غير قانوني، أو نقل فيروسات أو برامج ضارة، أو محاولة الوصول غير المصرح به، أو انتهاك حقوق الملكية الفكرية.'
        : 'Using the service for illegal purposes, transmitting viruses or malware, attempting unauthorized access, or infringing intellectual property rights is prohibited.'
    },
    {
      icon: Clock,
      title: isRTL ? 'الدفع والاشتراك' : 'Payment & Subscription',
      content: isRTL
        ? 'تُفوتر الاشتراكات مقدماً على أساس شهري أو سنوي. لا يمكن استرداد المبالغ المدفوعة. يمكنك إلغاء اشتراكك في أي وقت، وسيظل نشطاً حتى نهاية فترة الفوترة الحالية.'
        : 'Subscriptions are billed in advance on a monthly or annual basis. Payments are non-refundable. You may cancel at any time, and it will remain active until the end of the current billing period.'
    },
    {
      icon: AlertTriangle,
      title: isRTL ? 'حدود المسؤولية' : 'Limitation of Liability',
      content: isRTL
        ? 'لن تكون فلوفيد ERP مسؤولة عن أي أضرار غير مباشرة أو عرضية أو خاصة أو تبعية ناتجة عن استخدام أو عدم القدرة على استخدام خدماتنا.'
        : 'FlowFeed ERP shall not be liable for any indirect, incidental, special, or consequential damages resulting from use or inability to use our services.'
    }
  ];

  return (
    <div className={`min-h-screen bg-background ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* SEO Title */}
      <Helmet>
        <title>Terms of Service | FlowFeed POS</title>
      </Helmet>
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Link to="/">
              <Button variant="ghost" className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <ArrowLeft className="h-4 w-4" />
                {isRTL ? 'العودة للرئيسية' : 'Back to Home'}
              </Button>
            </Link>
            <h1 className="text-xl font-bold">{isRTL ? 'شروط الخدمة' : 'Terms of Service'}</h1>
            <div className="w-32" />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 dark:bg-purple-900/30 mb-4">
            <FileText className="h-8 w-8 text-purple-600" />
          </div>
          <h1 className="text-3xl font-bold mb-4">{isRTL ? 'شروط الخدمة' : 'Terms of Service'}</h1>
          <p className="text-muted-foreground">
            {isRTL ? 'آخر تحديث: ديسمبر 2024' : 'Last updated: December 2024'}
          </p>
        </div>

        <Card className="mb-8">
          <CardContent className="p-6">
            <p className="text-muted-foreground leading-relaxed">
              {isRTL 
                ? 'مرحباً بك في فلوفيد ERP. تحكم هذه الشروط والأحكام استخدامك لمنصتنا وخدماتنا. يرجى قراءتها بعناية قبل استخدام خدماتنا.'
                : 'Welcome to FlowFeed ERP. These terms and conditions govern your use of our platform and services. Please read them carefully before using our services.'
              }
            </p>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {sections.map((section, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="p-2 rounded-lg bg-primary/10">
                    <section.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div className={isRTL ? 'text-right' : ''}>
                    <h3 className="font-semibold text-lg mb-2">{section.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{section.content}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Contact */}
        <Card className="mt-8 bg-muted/50">
          <CardContent className="p-6 text-center">
            <Mail className="h-8 w-8 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-semibold mb-2">{isRTL ? 'أسئلة حول الشروط؟' : 'Questions about Terms?'}</h3>
            <p className="text-muted-foreground mb-4">
              {isRTL ? 'تواصل معنا على' : 'Contact us at'}
            </p>
            <a href="mailto:feed@maxefeed.com" className="text-primary hover:underline font-medium">
              feed@maxefeed.com
            </a>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default TermsOfService;
